import React from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiPercent } from "react-icons/fi";

const getSafeVideoSource = (value) => {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:") return null;

    if (url.hostname === "youtu.be") {
      const videoId = url.pathname.slice(1);
      return videoId ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}` : null;
    }
    if (
      ["youtube.com", "www.youtube.com", "youtube-nocookie.com", "www.youtube-nocookie.com"].includes(
        url.hostname,
      )
    ) {
      const videoId =
        url.searchParams.get("v") ||
        url.pathname.match(/^\/(?:embed|shorts)\/([A-Za-z0-9_-]+)/)?.[1];
      return videoId
        ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`
        : null;
    }
    if (url.hostname === "vimeo.com" || url.hostname === "www.vimeo.com") {
      const videoId = url.pathname.match(/^\/(\d+)/)?.[1];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
    }
    if (url.hostname === "player.vimeo.com") return url.href;
    if (/\.(mp4|webm)$/i.test(url.pathname)) return url.href;
  } catch (error) {
    return null;
  }
  return null;
};

const StorefrontContentBlock = ({ block, promotions = [], animation = "none" }) => {
  if (!block?.enabled) return null;
  const animationClass =
    animation === "fade" || animation === "slide"
      ? ` storefront-animation-${animation}`
      : "";

  if (block.type === "marquee") {
    if (!block.text?.trim()) return null;
    return (
      <section
        className={`storefront-content-block storefront-marquee${animationClass}`}
        aria-label="Store announcement"
      >
        <div className="storefront-marquee-track">
          <span>{block.text}</span>
          <span aria-hidden="true">{block.text}</span>
        </div>
      </section>
    );
  }

  if (block.type === "video") {
    const source = getSafeVideoSource(block.videoUrl);
    if (!source) return null;
    return (
      <section className={`storefront-content-block storefront-video-block${animationClass}`}>
        {block.title && <h2>{block.title}</h2>}
        {block.text && <p>{block.text}</p>}
        {/\.(mp4|webm)(?:$|[?#])/i.test(source) ? (
          <video controls preload="metadata" src={source}>
            Your browser does not support embedded video.
          </video>
        ) : (
          <iframe
            src={source}
            title={block.title || "Store video"}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}
      </section>
    );
  }

  if (block.type === "promotion") {
    const promotion =
      promotions.find(
        (item) =>
          String(item.PromotionID || "").toLowerCase() ===
          String(block.promotionId).toLowerCase(),
      ) || promotions[0];
    if (!promotion) return null;
    const discountText =
      promotion.DiscountType === "Percentage"
        ? `Save ${promotion.DiscountValue}%`
        : promotion.DiscountValue
          ? `Save R${promotion.DiscountValue}`
          : "Limited-time offer";

    return (
      <section className={`storefront-content-block storefront-promotion-block${animationClass}`}>
        <div className="storefront-promotion-icon"><FiPercent /></div>
        <div className="storefront-promotion-copy">
          <span>{discountText}</span>
          <h2>{block.title || promotion.Title || promotion.Name || "Special offer"}</h2>
          <p>{block.text || promotion.Description || discountText}</p>
        </div>
        {block.buttonText && (
          <StorefrontAction label={block.buttonText} destination={block.buttonLink || "/shop"} />
        )}
      </section>
    );
  }

  return null;
};

const StorefrontAction = ({ label, destination }) => {
  const target = destination || "/shop";
  if (target.startsWith("/") && !target.startsWith("//")) {
    return (
      <Link className="storefront-block-button" to={target}>
        {label} <FiArrowRight />
      </Link>
    );
  }
  try {
    if (new URL(target).protocol === "https:") {
      return (
        <a
          className="storefront-block-button"
          href={target}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label} <FiArrowRight />
        </a>
      );
      }
  } catch (error) {
    return null;
  }
  return null;
};

export default StorefrontContentBlock;
